import {
  ArrowRight,
  Check,
  Clock3,
  Eye,
  FileSearch,
  Globe,
  Layers3,
  ListOrdered,
  Search,
  ShieldAlert,
  Sparkles,
  UserCheck,
  Wand2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";
import { BrandMark } from "../components/Brand";
import { ThemeControl } from "../components/ThemeControl";
import {
  CheckViews,
  CompetitorDemo,
  ExportDemo,
  HeroReport,
  HonestDemo,
  PageDetailDemo,
  PipelineDemo,
  PriorityBoard,
  PrivateDemo,
  PromptDemo,
  SpamRiskPanel,
} from "./landing/LandingDemos";
import { FAQS, ORDER_URL, REPORT_PRICE, SAMPLE_AUDIT_AREAS, SAMPLE_PRIORITIES, SPAM_SIGNALS } from "./landing/landingData";
import { SampleReportOverlay } from "./landing/SampleReportOverlay";
import "./landing/landing.css";

const PAGE_TITLE = "Website Report by KittyLaunch | SEO, Spam Risk and Security Check for $29";
const PAGE_DESCRIPTION = "A KittyLaunch expert analyzes every page of your website with our own scanning system. Get one clear report on SEO, spam risk, technical health and security, with every fix ranked by impact. $29, delivered in 3 to 5 business days.";

const DELIVERY = "3 to 5 business days";

const INCLUDED = [
  "Hands-on analysis by a KittyLaunch expert",
  "SEO audit of every public page",
  "Spam and over-optimization risk check",
  "Technical SEO and site health",
  "Security headers and email authentication",
  "Top search competitor comparison",
  "Ranked action plan with AI-ready fix prompts",
  "Private report, locked to your email",
  "PDF, Markdown and copy to AI agent",
];

const STRUCTURED_DATA = JSON.stringify([
  {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "KittyLaunch Website Report",
    description: PAGE_DESCRIPTION,
    brand: { "@type": "Brand", name: "KittyLaunch" },
    offers: { "@type": "Offer", price: "29", priceCurrency: "USD", availability: "https://schema.org/InStock", url: ORDER_URL },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((item) => ({ "@type": "Question", name: item.question, acceptedAnswer: { "@type": "Answer", text: item.answer } })),
  },
]);

function normalizeWebsite(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    if (!["http:", "https:"].includes(url.protocol) || !/^[^.]+(\.[^.]+)+$/.test(url.hostname)) return null;
    return url.href.replace(/\/$/, "");
  } catch {
    return null;
  }
}

function usePageMeta() {
  useEffect(() => {
    const previousTitle = document.title;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    const previousDescription = meta.content;
    document.title = PAGE_TITLE;
    meta.content = PAGE_DESCRIPTION;
    return () => {
      document.title = previousTitle;
      if (created) meta.remove();
      else meta.content = previousDescription;
    };
  }, []);
}

function OrderForm({ inputRef, compact = false, id }: { inputRef?: RefObject<HTMLInputElement | null>; compact?: boolean; id: string }) {
  const [website, setWebsite] = useState("");
  const [error, setError] = useState(false);

  function submit(event: FormEvent) {
    event.preventDefault();
    const normalized = normalizeWebsite(website);
    if (!normalized) {
      setError(true);
      return;
    }
    window.location.assign(`${ORDER_URL}?website=${encodeURIComponent(normalized)}`);
  }

  return (
    <form className="lp-order" data-compact={compact || undefined} onSubmit={submit} noValidate>
      <label className="lp-order-field" data-error={error || undefined}>
        <Globe size={17} aria-hidden="true" />
        <span className="sr-only">Your website address</span>
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="url"
          autoComplete="url"
          placeholder="yourwebsite.com"
          value={website}
          aria-invalid={error || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={(event) => {
            setWebsite(event.target.value);
            if (error) setError(false);
          }}
        />
      </label>
      <button className="button button-primary" type="submit">Get my report <span className="lp-price-chip">{REPORT_PRICE}</span></button>
      {error && <p className="lp-order-error" id={`${id}-error`} role="alert">Enter your website address, for example yoursite.com</p>}
    </form>
  );
}

function SectionHead({ eyebrow, title, children, center = false }: { eyebrow: string; title: ReactNode; children?: ReactNode; center?: boolean }) {
  return (
    <div className="lp-head" data-center={center || undefined}>
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      {children && <p>{children}</p>}
    </div>
  );
}

function Points({ items }: { items: Array<{ icon: typeof Check; title: string; text: string }> }) {
  return (
    <ul className="lp-points">
      {items.map((item) => {
        const Icon = item.icon;
        return <li key={item.title}><span><Icon size={16} aria-hidden="true" /></span><div><strong>{item.title}</strong><p>{item.text}</p></div></li>;
      })}
    </ul>
  );
}

export function LandingPage() {
  usePageMeta();
  const [sampleOpen, setSampleOpen] = useState(() => window.location.hash === "#sample-report");
  const [showStickyCta, setShowStickyCta] = useState(false);
  const heroInputRef = useRef<HTMLInputElement>(null);
  const heroFormRef = useRef<HTMLDivElement>(null);
  const finalRef = useRef<HTMLElement>(null);
  const pricingRef = useRef<HTMLElement>(null);

  const openSample = useCallback(() => setSampleOpen(true), []);
  const closeSample = useCallback(() => setSampleOpen(false), []);
  const focusOrder = useCallback(() => {
    setSampleOpen(false);
    window.requestAnimationFrame(() => {
      heroFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      heroInputRef.current?.focus({ preventScroll: true });
    });
  }, []);

  useEffect(() => {
    const targets = [heroFormRef.current, pricingRef.current, finalRef.current].filter(Boolean) as Element[];
    const visible = new Set<Element>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      }
      const pastHero = (heroFormRef.current?.getBoundingClientRect().bottom ?? 0) < 0;
      setShowStickyCta(pastHero && visible.size === 0);
    });
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="lp" id="top">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: STRUCTURED_DATA }} />

      <header className="lp-header">
        <div className="lp-wrap lp-header-inner">
          <a href="#top" className="brand" aria-label="ReportGen by KittyLaunch">
            <BrandMark />
            <span className="brand-copy"><strong>ReportGen</strong><small>by KittyLaunch</small></span>
          </a>
          <nav className="lp-nav" aria-label="Page sections">
            <a href="#spam-risk">Spam risk</a>
            <a href="#checks">What we check</a>
            <a href="#how">How it works</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="lp-header-actions">
            <ThemeControl />
            <button className="button button-ghost lp-header-sample" type="button" onClick={openSample}><Eye size={15} />Sample</button>
            <button className="button button-primary" type="button" onClick={focusOrder}>Get my report</button>
          </div>
        </div>
      </header>

      <main>
        <section className="lp-hero">
          <div className="lp-wrap">
            <div className="lp-hero-copy">
              <span className="lp-hero-badge"><UserCheck size={14} aria-hidden="true" />Made by real experts, not just a scanner</span>
              <h1>Find out what's holding your website back.</h1>
              <p>A KittyLaunch expert analyzes every page of your site with the help of our own scanning system. You get one clear report on SEO, spam risk, technical health and security, with every fix ranked by impact.</p>
              <div className="lp-hero-form" ref={heroFormRef}>
                <OrderForm id="hero-website" inputRef={heroInputRef} />
                <button className="lp-sample-link" type="button" onClick={openSample}><Eye size={16} aria-hidden="true" />View a sample report</button>
              </div>
              <ul className="lp-ticks">
                <li><Check size={14} aria-hidden="true" />One-time payment</li>
                <li><Check size={14} aria-hidden="true" />Made by a real expert</li>
                <li><Check size={14} aria-hidden="true" />Delivered in {DELIVERY}</li>
              </ul>
            </div>
          </div>
          <div className="lp-wrap lp-hero-shot-wrap">
            <div className="lp-hero-shot" onClick={openSample}>
              <HeroReport />
              <span className="lp-note lp-note-score">Health score checked by our experts</span>
              <span className="lp-note lp-note-plan">Every fix ranked by impact</span>
              <button className="lp-shot-cta" type="button" onClick={(event) => { event.stopPropagation(); openSample(); }}><Eye size={16} aria-hidden="true" />Explore the full sample report</button>
            </div>
          </div>
        </section>

        <section className="lp-facts" aria-label="What you get">
          <div className="lp-wrap">
            <dl>
              <div><dt>20+</dt><dd>audit areas, from page titles to email security</dd></div>
              <div><dt>Every page</dt><dd>crawled and checked, not just your homepage</dd></div>
              <div><dt>Every finding</dt><dd>confirmed by hand by a KittyLaunch expert</dd></div>
              <div><dt>{REPORT_PRICE}</dt><dd>one-time payment, no subscription</dd></div>
            </dl>
          </div>
        </section>

        <section className="lp-section" id="report">
          <div className="lp-wrap lp-split">
            <div>
              <SectionHead eyebrow="Ranked action plan" title="Know exactly what to fix first.">
                Most audits hand you hundreds of warnings and leave you to figure it out. Your report starts with a short, ranked list. Each task shows its impact and how many pages it affects.
              </SectionHead>
              <Points items={[
                { icon: ListOrdered, title: "Ranked by impact", text: "High, medium and low, so you can plan your week." },
                { icon: Layers3, title: "Clear scope", text: "See at a glance whether a problem affects 2 pages or 200." },
                { icon: FileSearch, title: "Linked to the evidence", text: "Jump from each task straight to the pages behind it." },
              ]} />
            </div>
            <PriorityBoard onOpenSample={openSample} />
          </div>
        </section>

        <section className="lp-section lp-dark" id="spam-risk">
          <div className="lp-wrap">
            <div className="lp-spam-intro">
              <SectionHead eyebrow="Spam risk check" title={<>Is Google reading your pages as <span className="lp-accent">spam</span>?</>}>
                Repeating keywords, stuffing headings and publishing thin template pages can push you down in search, or out of it completely. Most site owners only notice when their traffic drops. We check every page for the patterns search engines treat as spam.
              </SectionHead>
              <button className="button button-primary lp-big-button" type="button" onClick={focusOrder}><ShieldAlert size={17} />Check my site for spam risk</button>
            </div>
            <div className="lp-spam-grid">
              <SpamRiskPanel onOpenSample={openSample} />
              <div className="lp-signals">
                <h3>Reasons for spam penalization we check</h3>
                <ol>
                  {SPAM_SIGNALS.map((signal, index) => (
                    <li key={signal.title}><span>{String(index + 1).padStart(2, "0")}</span><div><strong>{signal.title}</strong><p>{signal.text}</p></div></li>
                  ))}
                </ol>
                <p className="lp-signals-foot">Every flag shows the page, what we found and where, so you can fix it before it costs you rankings.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="lp-section" id="checks">
          <div className="lp-wrap">
            <SectionHead eyebrow="What we check" title="SEO, technical health and security. One report." center>
              Most tools only check one part of your site. We check all of it, so you see the full picture without paying for three different subscriptions.
            </SectionHead>
            <CheckViews />
          </div>
        </section>

        <section className="lp-section">
          <div className="lp-wrap lp-split lp-split-reverse">
            <PageDetailDemo />
            <div>
              <SectionHead eyebrow="Page-level detail" title="See every page that needs work.">
                A score alone does not tell you what to do. Your report lists every affected page with the exact issue, so you or your developer can start fixing right away.
              </SectionHead>
              <Points items={[
                { icon: Search, title: "Search and filter everything", text: "Every table is searchable, sortable and filterable." },
                { icon: Layers3, title: "Click a number, see the pages", text: "Each count opens the exact pages behind it." },
                { icon: Eye, title: "Easy to read anywhere", text: "Works on desktop and phone, in light or dark mode." },
              ]} />
            </div>
          </div>
        </section>

        <section className="lp-section">
          <div className="lp-wrap lp-split">
            <div>
              <SectionHead eyebrow="AI-ready fixes" title="Hand the fix to your AI coding tool.">
                Every section comes with a ready-made prompt that includes the evidence and the safety rules. Paste it into Cursor, Claude Code or ChatGPT inside your project and let it do the heavy lifting.
              </SectionHead>
              <Points items={[
                { icon: FileSearch, title: "Includes the real evidence", text: "The exact pages, values and findings from your report." },
                { icon: ShieldAlert, title: "Safe by design", text: "Tells the AI to confirm each issue before it changes any code." },
                { icon: Wand2, title: "Works with any AI tool", text: "Plain text you can paste anywhere. Try copying this one." },
              ]} />
            </div>
            <PromptDemo />
          </div>
        </section>

        <section className="lp-section">
          <div className="lp-wrap">
            <SectionHead eyebrow="Built to be used" title="Everything you need to act on it." center />
            <div className="lp-bento">
              <article className="lp-bento-wide">
                <div><h3>Know who you compete with</h3><p>See which sites win the searches you want, and how far ahead they are.</p></div>
                <CompetitorDemo />
              </article>
              <article>
                <div><h3>Private by default</h3><p>Your report is locked to your email address. Nobody else can open it, and it never appears in search results.</p></div>
                <PrivateDemo />
              </article>
              <article>
                <div><h3>Take it anywhere</h3><p>Download a clean PDF for your team, save it as Markdown, or copy the whole report straight into your AI agent.</p></div>
                <ExportDemo />
              </article>
              <article className="lp-bento-wide">
                <div><h3>Honest numbers</h3><p>We never invent data or pad the report with scary counts. If something could not be checked, the report says so.</p></div>
                <HonestDemo />
              </article>
            </div>
          </div>
        </section>

        <section className="lp-section" id="how">
          <div className="lp-wrap">
            <SectionHead eyebrow="How we build your report" title="Made by experts. Powered by our own system." center>
              A scanner alone raises hundreds of alarms and cannot tell what matters to your business. That is why every report is built by a KittyLaunch expert, who works through your site by hand while our system collects the data from every page.
            </SectionHead>
            <PipelineDemo />
            <p className="lp-pipeline-result"><Sparkles size={15} aria-hidden="true" />In our sample report: {SAMPLE_AUDIT_AREAS} audit areas checked, turned into {SAMPLE_PRIORITIES.length} priorities ranked by our expert.</p>
          </div>
        </section>

        <section className="lp-section" id="pricing" ref={pricingRef}>
          <div className="lp-wrap lp-pricing">
            <div>
              <SectionHead eyebrow="Pricing" title="One report. One price.">
                No subscription and no setup. Tell us your website and we take it from there.
              </SectionHead>
              <ol className="lp-steps">
                <li><span>1</span><div><strong>Enter your website</strong><p>Pay once. No account, plugin or code access needed.</p></div></li>
                <li><span>2</span><div><strong>Your expert gets to work</strong><p>Our system scans every page, then a KittyLaunch expert analyzes your site and builds your report.</p></div></li>
                <li><span>3</span><div><strong>Get your report in {DELIVERY}</strong><p>We email you a private link, plus PDF and Markdown downloads.</p></div></li>
              </ol>
            </div>
            <div className="lp-price-card">
              <div className="lp-price-top">
                <span>Website Report</span>
                <div className="lp-price"><strong>{REPORT_PRICE}</strong><small>one-time</small></div>
                <p>Less than one month of a typical SEO tool subscription.</p>
                <span className="lp-delivery"><Clock3 size={15} aria-hidden="true" />Delivered in {DELIVERY}</span>
              </div>
              <ul>
                {INCLUDED.map((item) => <li key={item}><Check size={15} aria-hidden="true" />{item}</li>)}
              </ul>
              <OrderForm id="pricing-website" compact />
              <button className="lp-sample-link" type="button" onClick={openSample}><Eye size={16} aria-hidden="true" />See a sample report first</button>
            </div>
          </div>
        </section>

        <section className="lp-section" id="faq">
          <div className="lp-wrap lp-faq">
            <div>
              <SectionHead eyebrow="FAQ" title="Questions, answered." />
              <button className="lp-sample-link" type="button" onClick={openSample}><Eye size={16} aria-hidden="true" />Still unsure? Look inside a sample report</button>
            </div>
            <div className="lp-faq-list">
              {FAQS.map((item, index) => (
                <details key={item.question} open={index === 0}>
                  <summary>{item.question}<span aria-hidden="true" /></summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section lp-dark lp-final" ref={finalRef}>
          <div className="lp-wrap">
            <SectionHead eyebrow="Get started" title="Your website, analyzed by experts, for $29." center>
              Get a clear, ranked plan for SEO, spam risk, technical health and security, delivered in {DELIVERY}.
            </SectionHead>
            <div className="lp-final-form">
              <OrderForm id="final-website" />
              <button className="lp-sample-link" type="button" onClick={openSample}><Eye size={16} aria-hidden="true" />View a sample report</button>
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-wrap">
          <span className="brand"><BrandMark /><span className="brand-copy"><strong>ReportGen</strong><small>by KittyLaunch</small></span></span>
          <p>© {new Date().getFullYear()} KittyLaunch. Website reports made by real experts.</p>
        </div>
      </footer>

      <div className="lp-sticky-cta" data-visible={showStickyCta || undefined} aria-hidden={!showStickyCta}>
        <span><strong>Website Report</strong><small>{REPORT_PRICE} one-time, ready in {DELIVERY}</small></span>
        <button className="button button-primary" type="button" tabIndex={showStickyCta ? 0 : -1} onClick={focusOrder}>Get my report <ArrowRight size={15} /></button>
      </div>

      <SampleReportOverlay open={sampleOpen} onClose={closeSample} onOrder={focusOrder} />
    </div>
  );
}
